import React, { useState } from 'react';
import styled, { createGlobalStyle } from 'styled-components';
import { PlayIcon, RefreshIcon, DocumentTextIcon, DownloadIcon, TicketIcon, SparklesIcon, FilterIcon, UsersIcon, EyeIcon, EyeOffIcon, ArrowsExpandIcon, CheckIcon, XIcon, MenuIcon } from '@heroicons/react/outline';
import { Tooltip, Switch } from 'antd';
import { Modal } from '../../design-system/components/feedback/Modal';
import { colors, spacing, radius } from '../../design-system/tokens';
import type { TableColumn } from './types';

interface TableActionBarProps {
  tableName?: string;
  onRunAllEmpty?: () => void;
  onRefreshAll?: () => void;
  onManageTemplate?: () => void;
  onExport?: () => void;
  onRefresh?: () => void;
  
  // Companion
  companionEnabled?: boolean;
  companionSettingsButton?: React.ReactNode;
  
  // Companion Skills (MOCK - configured per table)
  companionSkills?: {
    draftResponses?: boolean;
    updateCells?: boolean;
    generateCodes?: boolean;
  };
  onDraftResponses?: () => void;
  onUpdateCells?: () => void;
  onGenerateCodes?: () => void;
  
  // Workflow filtering
  workflowEnabled?: boolean;
  showActiveOnly?: boolean;
  onWorkflowFilterChange?: (showActiveOnly: boolean) => void;
  
  // Column management
  hiddenColumns?: Set<string>;
  allColumns?: TableColumn[];
  onShowColumn?: (columnId: string) => void;
  onHideColumn?: (columnId: string) => void;
  onShowAllColumns?: () => void;
  onHiddenColumnsChange?: (hiddenColumns: Set<string>) => void;
  onReorderColumns?: (newColumns: TableColumn[]) => void;
}

const ActionBarContainer = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 6px 12px;
  background: #ffffff;
  border-top: 1px solid rgba(0, 0, 0, 0.06);
  border-bottom: 1px solid rgba(0, 0, 0, 0.06);
  gap: 16px;
  width: 100%;
  max-width: 100%;
  box-sizing: border-box;
  flex-shrink: 0;
  position: sticky;
  left: 0;
  z-index: 2;
`;

const ActionBarLeft = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
`;

const TableName = styled.div`
  font-size: 12px;
  font-weight: 500;
  color: ${colors.text.primary};
`;

const ActionBarRight = styled.div`
  display: flex;
  align-items: center;
  gap: 6px;
`;

const ActionButton = styled.button<{ $variant?: 'default' | 'primary' | 'companion' }>`
  display: flex;
  align-items: center;
  gap: 5px;
  padding: 6px 12px;
  border-radius: 6px;
  font-size: 12px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s;
  border: none;

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }

  ${({ $variant = 'default' }) => {
    if ($variant === 'primary') {
      return `
        background: ${colors.brand?.primary || '#495057'};
        color: white;
        &:hover:not(:disabled) {
          background: ${colors.brand?.primaryHover || '#3d4349'};
        }
      `;
    } else if ($variant === 'companion') {
      return `
        background: #3b82f6;
        color: white;
        &:hover:not(:disabled) {
          background: #2563eb;
        }
      `;
    } else {
      return `
        background: white;
        color: ${colors.text.primary};
        border: 1px solid ${colors.border?.secondary || 'rgba(0, 0, 0, 0.08)'};
        &:hover:not(:disabled) {
          background: ${colors.background?.secondary || '#f8f9fa'};
        }
        &:disabled {
          background: #fafafa;
          color: ${colors.text.tertiary};
          border-color: #e6e6e6;
        }
      `;
    }
  }}

  svg {
    width: 14px;
    height: 14px;
  }
`;

const WorkflowFilter = styled.div`
  display: flex;
  align-items: center;
  gap: 6px;
  padding: 4px 8px;
  background: transparent;
  border: 1px solid ${colors.border.secondary};
  border-radius: 4px;
  font-size: 11px;
  color: ${colors.text.secondary};
  transition: all 0.15s ease;
  
  &:hover {
    background: ${colors.background.hover};
    border-color: ${colors.border.primary};
  }
`;

const FilterLabel = styled.span`
  font-weight: 400;
  display: flex;
  align-items: center;
  gap: 4px;
  
  svg {
    width: 12px;
    height: 12px;
  }
`;

const FilterToggle = styled.div`
  display: flex;
  align-items: center;
  gap: 4px;
  font-size: 10px;
  color: ${colors.text.secondary};
`;

const ColumnOrderList = styled.div`
  max-height: 400px;
  overflow-y: auto;
  padding: 4px 0;
  
  &::-webkit-scrollbar {
    width: 3px;
  }
  
  &::-webkit-scrollbar-track {
    background: transparent;
  }
  
  &::-webkit-scrollbar-thumb {
    background: rgba(0, 0, 0, 0.1);
    border-radius: 2px;
  }
`;

const ColumnOrderItem = styled.div<{ $isFirst?: boolean; $isLast?: boolean }>`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 8px 12px;
  margin-bottom: 2px;
  background: transparent;
  transition: all 0.15s ease;
  border-radius: 4px;
  
  &:hover {
    background: rgba(0, 0, 0, 0.02);
  }
`;

const ColumnOrderInfo = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
  flex: 1;
  min-width: 0;
`;

const ColumnOrderNumber = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 24px;
  height: 24px;
  border-radius: 4px;
  background: rgba(0, 0, 0, 0.04);
  font-size: 11px;
  font-weight: 600;
  color: ${colors.text.secondary};
  flex-shrink: 0;
`;

const ColumnOrderName = styled.span`
  font-size: 13px;
  font-weight: 400;
  color: ${colors.text.primary};
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

const ColumnOrderActions = styled.div`
  display: flex;
  align-items: center;
  gap: 2px;
  opacity: 0.6;
  transition: opacity 0.15s ease;
  
  ${ColumnOrderItem}:hover & {
    opacity: 1;
  }
`;

const MoveButton = styled.button<{ $disabled?: boolean }>`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 24px;
  height: 24px;
  border: none;
  border-radius: 4px;
  background: transparent;
  cursor: ${props => props.$disabled ? 'not-allowed' : 'pointer'};
  opacity: ${props => props.$disabled ? 0.3 : 1};
  color: ${colors.text.secondary};
  transition: all 0.15s ease;
  
  &:hover:not(:disabled) {
    background: rgba(0, 0, 0, 0.06);
    color: ${colors.text.primary};
  }
  
  svg {
    width: 14px;
    height: 14px;
  }
`;

// Modal styles matching CreatorModal EXACTLY
const ColumnModalStyles = createGlobalStyle`
  .column-management-modal {
    background: ${colors.palette?.gray?.[100] || '#f8f9fa'} !important;
    top: 2% !important;
    left: 50% !important;
    transform: translateX(-50%) !important;
    height: 96% !important;
    max-height: 96% !important;
    
    /* Remove default modal body padding */
    & > div:last-child {
      padding: 0 !important;
    }
  }
`;

const ModalContent = styled.div`
  display: flex;
  flex-direction: column;
  height: 100%;
`;

const TopPanel = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: ${spacing?.[2] || '8px'} ${spacing?.[4] || '16px'};
  background: ${colors.palette?.gray?.[25] || '#fafafa'};
  border-bottom: 1px solid rgba(0, 0, 0, 0.02);
  flex-shrink: 0;
`;

const TitleSection = styled.div`
  flex: 1;
  max-width: 300px;
  
  h3 {
    margin: 0;
    font-size: 14px;
    font-weight: 600;
    color: ${colors.text.primary};
  }
`;

const ActionButtons = styled.div`
  display: flex;
  gap: ${spacing?.[1] || '4px'};
  align-items: center;
`;

const SaveButton = styled.button`
  background: transparent;
  color: ${colors.text.primary};
  border: none;
  border-radius: 4px;
  padding: 4px 12px;
  font-size: 12px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s ease;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 4px;
  line-height: 1;
  
  &:hover:not(:disabled) {
    background: ${colors.background?.hover || 'rgba(0, 0, 0, 0.04)'};
  }
  
  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
  
  svg {
    width: 12px;
    height: 12px;
    flex-shrink: 0;
  }
`;

const SecondaryButton = styled.button`
  background: transparent;
  color: ${colors.text.secondary};
  border: none;
  border-radius: 4px;
  padding: 4px 12px;
  font-size: 12px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s ease;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 4px;
  line-height: 1;
  
  &:hover:not(:disabled) {
    background: ${colors.background?.hover || 'rgba(0, 0, 0, 0.04)'};
    color: ${colors.text.primary};
  }
  
  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;

const ContentArea = styled.div`
  flex: 1;
  background: ${colors.palette?.gray?.[100] || '#f8f9fa'};
  padding: ${spacing?.[2] || '8px'} ${spacing?.[8] || '32px'};
  display: flex;
  justify-content: center;
  overflow-y: auto;
`;

const ContentSheet = styled.div`
  width: 100%;
  max-width: 1000px;
  background: white;
  border-radius: 8px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
  min-height: 600px;
  display: flex;
  flex-direction: column;
  padding: ${spacing?.[6] || '24px'};
`;

const Section = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${spacing?.[3] || '12px'};
  margin-bottom: ${spacing?.[8] || '32px'};
  
  &:last-child {
    margin-bottom: 0;
  }
`;

const SectionHeader = styled.div`
  margin-bottom: ${spacing?.[4] || '16px'};
`;

const SectionTitle = styled.h3`
  margin: 0;
  font-size: 14px;
  font-weight: 500;
  color: ${colors.text.primary};
  display: flex;
  align-items: center;
  gap: ${spacing?.[2] || '8px'};
  padding-bottom: ${spacing?.[2] || '8px'};
  border-bottom: 1px solid ${colors.border?.secondary || 'rgba(0, 0, 0, 0.08)'};
`;

const ItemsList = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${spacing?.[2] || '8px'};
`;

const ItemCard = styled.div<{ $isDragging?: boolean; $isHidden?: boolean; $isDragOver?: boolean }>`
  border: 1px solid ${colors.border?.secondary || 'rgba(0, 0, 0, 0.08)'};
  border-radius: 8px;
  padding: ${spacing?.[3] || '12px'};
  background: ${props => {
    if (props.$isDragging) return colors.palette?.blue?.[50] || '#eff6ff';
    if (props.$isDragOver) return '#f0f9ff'; // Light blue for drag over
    if (props.$isHidden) return colors.palette?.gray?.[50] || '#f9fafb';
    return 'white';
  }};
  display: flex;
  align-items: center;
  gap: ${spacing?.[3] || '12px'};
  transition: transform 0.2s ease, background 0.2s ease, border-color 0.2s ease, box-shadow 0.2s ease;
  opacity: ${props => props.$isHidden ? 0.6 : 1};
  cursor: ${props => props.$isDragging ? 'grabbing' : 'grab'};
  transform: ${props => props.$isDragging ? 'scale(1.02) rotate(2deg)' : 'scale(1)'};
  box-shadow: ${props => props.$isDragging ? '0 4px 12px rgba(0, 0, 0, 0.15)' : '0 0 0 rgba(0, 0, 0, 0)'};
  
  ${props => props.$isDragOver && `
    border-color: ${colors.palette?.blue?.[300] || '#93c5fd'};
    border-style: dashed;
  `}
  
  &:hover {
    border-color: ${colors.border?.primary || 'rgba(0, 0, 0, 0.12)'};
    box-shadow: 0 1px 3px rgba(0, 0, 0, 0.05);
  }
`;

const DragHandle = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 32px;
  height: 32px;
  border-radius: 6px;
  background: ${colors.palette?.gray?.[100] || '#f3f4f6'};
  color: ${colors.text.secondary};
  cursor: grab;
  flex-shrink: 0;
  transition: all 0.2s ease;
  
  &:hover {
    background: ${colors.palette?.gray?.[200] || '#e5e7eb'};
    color: ${colors.text.primary};
  }
  
  &:active {
    cursor: grabbing;
  }
  
  svg {
    width: 16px;
    height: 16px;
  }
`;

const ColumnInfo = styled.div`
  flex: 1;
  min-width: 0;
`;

const ColumnName = styled.div`
  font-weight: 500;
  color: ${colors.text.primary};
  font-size: 14px;
  line-height: 1.3;
`;

const ColumnMeta = styled.div`
  font-size: 12px;
  color: ${colors.text.secondary};
  line-height: 1.2;
  margin-top: 2px;
`;

const ItemActions = styled.div`
  display: flex;
  align-items: center;
  gap: ${spacing?.[1] || '4px'};
`;

const ToggleButton = styled.button<{ $isActive?: boolean }>`
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 6px 12px;
  border: none;
  border-radius: 6px;
  background: ${props => props.$isActive ? colors.palette?.green?.[100] || '#dcfce7' : colors.palette?.gray?.[100] || '#f3f4f6'};
  color: ${props => props.$isActive ? colors.palette?.green?.[700] || '#15803d' : colors.text.secondary};
  cursor: pointer;
  transition: all 0.2s ease;
  font-size: 12px;
  font-weight: 500;
  gap: 4px;
  
  &:hover {
    background: ${props => props.$isActive ? colors.palette?.green?.[200] || '#bbf7d0' : colors.palette?.gray?.[200] || '#e5e7eb'};
  }
  
  svg {
    width: 14px;
    height: 14px;
  }
`;


export const TableActionBar: React.FC<TableActionBarProps> = ({
  tableName,
  onRunAllEmpty,
  onRefreshAll,
  onManageTemplate,
  onExport,
  onRefresh,
  companionEnabled = false,
  companionSettingsButton,
  companionSkills = {},
  onDraftResponses,
  onUpdateCells,
  onGenerateCodes,
  workflowEnabled = false,
  showActiveOnly = false,
  onWorkflowFilterChange,
  hiddenColumns = new Set(),
  allColumns = [],
  onShowColumn,
  onHideColumn,
  onShowAllColumns,
  onHiddenColumnsChange,
  onReorderColumns
}) => {
  const hasHiddenColumns = hiddenColumns.size > 0;
  const [columnOrderModalOpen, setColumnOrderModalOpen] = useState(false);
  const [columnVisibilityModalOpen, setColumnVisibilityModalOpen] = useState(false);
  const [tempColumns, setTempColumns] = useState<TableColumn[]>(allColumns);
  const [tempHiddenColumns, setTempHiddenColumns] = useState<Set<string>>(new Set());
  const [draggedIndex, setDraggedIndex] = useState<number | null>(null);
  const [dragOverIndex, setDragOverIndex] = useState<number | null>(null);
  
  const handleDragStart = (index: number) => {
    setDraggedIndex(index);
  };

  const handleDragOver = (e: React.DragEvent, index: number) => {
    e.preventDefault();
    if (draggedIndex === null || draggedIndex === index) return;
    
    setDragOverIndex(index);
    
    // Live preview: reorder the array while dragging
    const newColumns = [...tempColumns];
    const draggedItem = newColumns[draggedIndex];
    
    // Remove from old position
    newColumns.splice(draggedIndex, 1);
    
    // Insert at new position
    newColumns.splice(index, 0, draggedItem);
    
    setTempColumns(newColumns);
    setDraggedIndex(index); // Update dragged index to follow the item
  };

  const handleDragLeave = () => {
    // Don't clear drag over immediately to prevent flicker
  };

  const handleDrop = (e: React.DragEvent, dropIndex: number) => {
    e.preventDefault();
    setDraggedIndex(null);
    setDragOverIndex(null);
  };

  const handleDragEnd = () => {
    setDraggedIndex(null);
    setDragOverIndex(null);
  };
  
  const handleSaveColumnOrder = () => {
    if (onReorderColumns) {
      onReorderColumns(tempColumns);
    }
    setColumnOrderModalOpen(false);
  };
  
  const handleOpenColumnOrderModal = () => {
    setTempColumns([...allColumns]);
    setColumnOrderModalOpen(true);
  };

  const handleOpenColumnVisibilityModal = () => {
    setTempHiddenColumns(new Set(hiddenColumns));
    setColumnVisibilityModalOpen(true);
  };

  const handleToggleColumnVisibility = (columnId: string) => {
    const newHiddenColumns = new Set(tempHiddenColumns);
    if (newHiddenColumns.has(columnId)) {
      newHiddenColumns.delete(columnId);
    } else {
      newHiddenColumns.add(columnId);
    }
    setTempHiddenColumns(newHiddenColumns);
  };

  const handleSaveColumnVisibility = async () => {
    console.log('💾 [TableActionBar] Save button clicked, tempHiddenColumns:', Array.from(tempHiddenColumns));
    console.log('💾 [TableActionBar] Current hiddenColumns:', Array.from(hiddenColumns));
    console.log('💾 [TableActionBar] onHiddenColumnsChange exists?', !!onHiddenColumnsChange);
    
    // Just call onHiddenColumnsChange which will trigger the backend save and reload
    if (onHiddenColumnsChange) {
      console.log('💾 [TableActionBar] Calling onHiddenColumnsChange with:', Array.from(tempHiddenColumns));
      onHiddenColumnsChange(tempHiddenColumns);
    } else {
      console.error('❌ [TableActionBar] onHiddenColumnsChange is not defined!');
    }
    
    setColumnVisibilityModalOpen(false);
  };
  
  return (
    <ActionBarContainer>
      <ActionBarLeft>
        {tableName && <TableName>{tableName}</TableName>}
{/*         
        {companionSettingsButton && (
          <>
            {companionSettingsButton}
          </>
        )} */}
        
        {/* Workflow Filter */}
        {workflowEnabled && onWorkflowFilterChange && (
          <WorkflowFilter>
            <FilterLabel>
              <FilterIcon />
              In Progress Only
            </FilterLabel>
            <Tooltip title={showActiveOnly ? 'Showing only creators with "In Progress" status' : 'Show creators with "In Progress" status for this stage'}>
              <Switch
                size="small"
                checked={showActiveOnly}
                onChange={onWorkflowFilterChange}
              />
            </Tooltip>
          </WorkflowFilter>
        )}
      </ActionBarLeft>
      
        <ActionBarRight>
          {/* Column Visibility Button - Always visible */}
          {allColumns.length > 0 && (
            <Tooltip title="Show/hide columns">
              <ActionButton onClick={handleOpenColumnVisibilityModal}>
                <EyeIcon />
                Columns
              </ActionButton>
            </Tooltip>
          )}
        
          {/* Column Order Button */}
          {onReorderColumns && allColumns.length > 0 && (
            <Tooltip title="Reorder columns">
              <ActionButton onClick={handleOpenColumnOrderModal}>
                <ArrowsExpandIcon />
                Reorder
              </ActionButton>
            </Tooltip>
          )}
      
        {/* Companion Skills Section */}
        {companionSkills.generateCodes && onGenerateCodes && (
          <>
            <Tooltip title="Generate Shopify promo codes with Campaign Carl">
              <ActionButton $variant="companion" onClick={onGenerateCodes}>
                <TicketIcon />
                Generate Codes
              </ActionButton>
            </Tooltip>
  
          </>
        )}

        {onRefresh && (
          <Tooltip title="Refresh table data">
            <ActionButton onClick={onRefresh}>
              <RefreshIcon />
            </ActionButton>
          </Tooltip>
        )}

        {onExport && (
          <Tooltip title="Export table as CSV file">
            <ActionButton onClick={onExport}>
              <DownloadIcon />
              Export
            </ActionButton>
          </Tooltip>
        )}

        {onRefreshAll && (
          <Tooltip title="Re-run all cells">
            <ActionButton onClick={onRefreshAll}>
              <RefreshIcon />
              Refresh All
            </ActionButton>
          </Tooltip>
        )}

        {onRunAllEmpty && (
          <Tooltip title="Run AI on all empty cells">
            <ActionButton $variant="primary" onClick={onRunAllEmpty}>
              <PlayIcon />
              Run All Empty
            </ActionButton>
          </Tooltip>
        )}
      </ActionBarRight>
      
      {/* Column Visibility Modal */}
      <ColumnModalStyles />
      <Modal
        isOpen={columnVisibilityModalOpen}
        onClose={() => setColumnVisibilityModalOpen(false)}
        size="full"
        height="full"
        showCloseButton={false}
        className="column-management-modal"
      >
        <ModalContent>
          <TopPanel>
            <TitleSection>
              <h3>Show/Hide Columns</h3>
            </TitleSection>
            <ActionButtons>
              <SecondaryButton
                onClick={() => setColumnVisibilityModalOpen(false)}
              >
                <XIcon />
                Cancel
              </SecondaryButton>
              <SaveButton onClick={handleSaveColumnVisibility}>
                <CheckIcon />
                Save Changes
              </SaveButton>
            </ActionButtons>
          </TopPanel>
          
          <ContentArea>
            <ContentSheet>
              <Section>
                <SectionHeader>
                  <SectionTitle>
                    <EyeIcon style={{ width: '16px', height: '16px' }} />
                    Column Visibility
                  </SectionTitle>
                </SectionHeader>
                
                <ItemsList>
                  {allColumns.map((column) => {
                    const isHidden = tempHiddenColumns.has(column.id);
                    return (
                      <ItemCard key={column.id} $isHidden={isHidden}>
                        <ColumnInfo>
                          <ColumnName>{column.name}</ColumnName>
                          <ColumnMeta>Column</ColumnMeta>
                        </ColumnInfo>
                        <ItemActions>
                          <ToggleButton
                            $isActive={!isHidden}
                            onClick={() => handleToggleColumnVisibility(column.id)}
                          >
                            {isHidden ? (
                              <>
                                <EyeOffIcon />
                                Hidden
                              </>
                            ) : (
                              <>
                                <EyeIcon />
                                Visible
                              </>
                            )}
                          </ToggleButton>
                        </ItemActions>
                      </ItemCard>
                    );
                  })}
                </ItemsList>
              </Section>
            </ContentSheet>
          </ContentArea>
        </ModalContent>
      </Modal>

      {/* Column Order Modal */}
      <Modal
        isOpen={columnOrderModalOpen}
        onClose={() => setColumnOrderModalOpen(false)}
        size="full"
        height="full"
        showCloseButton={false}
        className="column-management-modal"
      >
        <ModalContent>
          <TopPanel>
            <TitleSection>
              <h3>Reorder Columns</h3>
            </TitleSection>
            <ActionButtons>
              <SecondaryButton
                onClick={() => setColumnOrderModalOpen(false)}
              >
                <XIcon />
                Cancel
              </SecondaryButton>
              <SaveButton onClick={handleSaveColumnOrder}>
                <CheckIcon />
                Save Order
              </SaveButton>
            </ActionButtons>
          </TopPanel>
          
          <ContentArea>
            <ContentSheet>
              <Section>
                <SectionHeader>
                  <SectionTitle>
                    <ArrowsExpandIcon style={{ width: '16px', height: '16px' }} />
                    Column Order
                  </SectionTitle>
                </SectionHeader>
                
                <ItemsList>
                  {tempColumns.map((column, index) => (
                    <ItemCard
                      key={column.id}
                      draggable
                      onDragStart={() => handleDragStart(index)}
                      onDragOver={(e) => handleDragOver(e, index)}
                      onDragLeave={handleDragLeave}
                      onDrop={(e) => handleDrop(e, index)}
                      onDragEnd={handleDragEnd}
                      $isDragging={draggedIndex === index}
                      $isDragOver={dragOverIndex === index && draggedIndex !== index}
                    >
                      <DragHandle>
                        <MenuIcon />
                      </DragHandle>
                      <ColumnInfo>
                        <ColumnName>{column.name}</ColumnName>
                        <ColumnMeta>Position {index + 1} of {tempColumns.length}</ColumnMeta>
                      </ColumnInfo>
                    </ItemCard>
                  ))}
                </ItemsList>
              </Section>
            </ContentSheet>
          </ContentArea>
        </ModalContent>
      </Modal>
    </ActionBarContainer>
  );
};

